import { Alert, Box, Button, InputLabel, TextField, Typography } from "@mui/material";
import { Formik, FormikHelpers, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./ForgotPasswordRequest.styles";
import { IForgotPasswordRequestValues } from "./ForgotPasswordRequestInterfaces";
import { forgotPasswordRequestFormValidationSchema } from "validations/schemas";
import { useMemo, useState } from "react";
import { CORPORATE_BACKOFFICE_PATHS, RESET_PASSWORD_ERROR_CODES, ServerResponseErrorCodes } from "constants/constants";
import { forgotPasswordRequest } from "api/queries/usersAPI";

const ForgotPasswordRequest = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const classes = useStyles();
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [isEmailAlreadySent, setIsEmailAlreadySent] = useState(false);

    const locale = useMemo(() => i18n.language.slice(0, 2).toLowerCase(), [i18n.language]);

    const initFormValues: IForgotPasswordRequestValues = {
        email: "",
    };

    const handleChangePasswordSubmit = async (
        values: IForgotPasswordRequestValues,
        formikHelpers: FormikHelpers<IForgotPasswordRequestValues>,
    ) => {
        setIsSendingEmail(true);
        const newValues = { ...values, locale };
        try {
            await forgotPasswordRequest(newValues);
            setIsEmailSent(true);
            setIsSendingEmail(false);
        } catch (error: any) {
            const { response } = error;

            if (
                response &&
                response.status === ServerResponseErrorCodes.BAD_REQUEST &&
                response.data.errorCode &&
                response.data.errorCode === RESET_PASSWORD_ERROR_CODES.EMAIL_ALREADY_SENT
            ) {
                setIsEmailAlreadySent(true);
            }
            if (response && response.status === ServerResponseErrorCodes.UNAUTHORIZED) {
                formikHelpers.setFieldError("email", t("field_errors.invalid_email"));
            }
            setIsSendingEmail(false);
        }
    };

    return (
        <section>
            <section className={classes.mainWrapper}>
                <div className={classes.contentWrapper}>
                    <Typography className={classes.headerTitle}>{t("forgot_password_request.title")}</Typography>
                    {!isEmailSent && !isEmailAlreadySent && (
                        <>
                            <Alert severity="info" className={classes.alert}>
                                <Typography>{t("forgot_password_request.enter_your_email")}</Typography>
                            </Alert>

                            <Formik
                                initialValues={initFormValues}
                                validationSchema={forgotPasswordRequestFormValidationSchema}
                                onSubmit={handleChangePasswordSubmit}
                            >
                                {({ errors, touched, values, handleChange }) => {
                                    return (
                                        <Form className={classes.formContainer}>
                                            <>
                                                <Box className={classes.boxContainer}>
                                                    <InputLabel id="current-email-label">
                                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                                            {t("forgot_password_request.email")}
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        aria-labelledby="current-email-label"
                                                        fullWidth
                                                        id="current-email-input"
                                                        className={classes.textField}
                                                        name="email"
                                                        type="text"
                                                        variant="outlined"
                                                        disabled={isSendingEmail}
                                                        autoFocus={true}
                                                        value={values.email}
                                                        onChange={handleChange}
                                                        error={touched.email && Boolean(errors.email)}
                                                        helperText={touched.email && errors.email && t(errors.email)}
                                                    />
                                                </Box>

                                                <div className={classes.footer}>
                                                    <Button
                                                        variant="outlined"
                                                        color="info"
                                                        className={classes.submitButton}
                                                        onClick={() => navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN)}
                                                        disabled={isSendingEmail}
                                                    >
                                                        {t("forgot_password_request.back_to_login")}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="info"
                                                        type="submit"
                                                        className={classes.submitButton}
                                                        disabled={isSendingEmail}
                                                    >
                                                        {t("forgot_password_request.submit")}
                                                    </Button>
                                                </div>
                                            </>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </>
                    )}
                    {!!isEmailSent && (
                        <>
                            <Alert severity="success" className={classes.alert}>
                                <Typography>{t("forgot_password_request.email_sent")}</Typography>
                            </Alert>
                            <Button
                                variant="outlined"
                                color="info"
                                className={classes.submitButton}
                                onClick={() => navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN)}
                            >
                                {t("forgot_password_request.back_to_login")}
                            </Button>
                        </>
                    )}
                    {!!isEmailAlreadySent && (
                        <>
                            <Alert severity="error" className={classes.alert}>
                                <Typography>{t("forgot_password_request.email_already_sent")}</Typography>
                            </Alert>
                            <Button
                                variant="outlined"
                                color="info"
                                className={classes.submitButton}
                                onClick={() => navigate(CORPORATE_BACKOFFICE_PATHS.LOGIN)}
                            >
                                {t("forgot_password_request.back_to_login")}
                            </Button>
                        </>
                    )}
                </div>
            </section>
        </section>
    );
};

export default ForgotPasswordRequest;
